/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Cross2Icon,
  PersonIcon,
  CheckCircledIcon,
  MixIcon,
  IdCardIcon,
  UploadIcon,
} from '@/shared/ui/radix-icons';
import {
  ClientFilters,
  ClientFilterPanelProps,
  SectionHeader,
  ChipBtn,
} from './ClientFilterPanel.parts';
import { Text, UnstyledButton, FilterPanelShell } from '@/shared/ui';
import {
  AddressSection,
  IncomeSection,
  PreferenceSection,
} from './ClientFilterPanel.sections';

export type { ClientFilters } from './ClientFilterPanel.parts';

export const ClientFilterPanel: React.FC<ClientFilterPanelProps> = ({
  open,
  onClose,
  onApply,
  onClear,
  initialFilters = {},
  brokers = [],
}) => {
  const { t } = useTranslation('features/clients');
  const [filters, setFilters] = useState<ClientFilters>(initialFilters);

  // Re-seed the draft from `initialFilters` each time the panel opens. The
  // list header's chip row clears every applied filter ("Limpar todos")
  // without going through this panel, so a reopened panel must mirror what is
  // actually applied rather than keep a stale pre-clear selection.
  useEffect(() => {
    if (open) setFilters(initialFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);


  const activeCount = Object.values(filters).filter((v) => !!v).length;
  const patch = (next: Partial<ClientFilters>) => setFilters({ ...filters, ...next });

  if (!open) return null;

  return (
    <FilterPanelShell
      testIdPrefix="client-"
      layout="sibling"
      title={t('filterPanel.title')}
      onClose={onClose}
      onClear={() => { setFilters({}); onClear(); }}
      onApply={() => { onApply(filters); onClose(); }}
      activeCount={activeCount}
      clearLabel={t('filterPanel.clear')}
      applyLabel={t('filterPanel.apply', { count: activeCount })}
      closeIcon={<Cross2Icon width={14} height={14} />}
      ButtonComponent={UnstyledButton}
      TextComponent={({ children, style }) => (
        <Text component="span" style={style ?? { fontSize: 'var(--text-body-sm)', fontWeight: 'var(--font-bold)' as unknown as number, color: 'var(--seed-base)' }}>
          {children}
        </Text>
      )}
      backdropStyle={{
        /*
         * The whole viewport, like every other filter panel. It used to
         * start one `--header-height` down, from when a global top bar sat
         * above these routes; the agency shell draws its own now, so the
         * offset left this panel floating below a strip of page while the
         * listings panel beside it covered the screen.
         */
        position: 'fixed',
        inset: 0,
        background: 'var(--overlay-scrim)',
        backdropFilter: 'blur(2px)',
        zIndex: 100,
      }}
      panelStyle={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: '340px',
        maxWidth: '90vw',
        background: 'var(--surface-primary)',
        boxShadow: 'var(--shadow-lg)',
        zIndex: 101,
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'var(--font-family)',
        overflow: 'hidden',
      }}
      headerStyle={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 'var(--space-3) var(--space-4)',
        background: 'color-mix(in srgb, var(--seed-base) 5%, var(--surface-primary))',
        borderBottom: '1px solid color-mix(in srgb, var(--seed-base) 10%, transparent)',
      }}
      closeButtonStyle={{
        width: 28,
        height: 28,
        borderRadius: 'var(--radius-md)',
        border: 'none',
        background: 'transparent',
        color: 'var(--text-tertiary)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
      }}
      bodyStyle={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}
      footerStyle={{ display: 'flex', gap: 'var(--space-2)', padding: 'var(--space-3) var(--space-4)', borderTop: '1px solid var(--border-subtle)' }}
      clearButtonStyle={{
        flex: 1,
        padding: 'var(--space-2)',
        fontSize: 'var(--text-meta)',
        fontWeight: 'var(--font-semibold)' as unknown as number,
        background: 'transparent',
        color: 'var(--text-secondary)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        cursor: 'pointer',
        fontFamily: 'var(--font-family)',
      }}
      applyButtonStyle={{
        flex: 1,
        padding: 'var(--space-2)',
        fontSize: 'var(--text-meta)',
        fontWeight: 'var(--font-bold)' as unknown as number,
        background: 'var(--button-primary-bg)',
        color: 'var(--button-primary-text)',
        border: 'none',
        borderRadius: 'var(--radius-md)',
        cursor: 'pointer',
        fontFamily: 'var(--font-family)',
      }}
    >
      {/* Tipo de Cliente */}
          <div>
            <SectionHeader icon={<PersonIcon width={14} height={14} />} title={t('filterPanel.clientType')} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
              <ChipBtn
                value={t('filterPanel.individual')}
                active={filters.type === 'individual'}
                onClick={() => setFilters({ ...filters, type: filters.type === 'individual' ? '' : 'individual' })}
              />
              <ChipBtn
                value={t('filterPanel.company')}
                active={filters.type === 'company'}
                onClick={() => setFilters({ ...filters, type: filters.type === 'company' ? '' : 'company' })}
              />
            </div>
          </div>

          {/* Papel */}
          <div>
            <SectionHeader icon={<MixIcon width={14} height={14} />} title={t('filterPanel.role')} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
              <ChipBtn
                value={t('filterPanel.roleDemand')}
                active={filters.role === 'demand'}
                onClick={() => setFilters({ ...filters, role: filters.role === 'demand' ? '' : 'demand' })}
              />
              <ChipBtn
                value={t('filterPanel.roleSupply')}
                active={filters.role === 'supply'}
                onClick={() => setFilters({ ...filters, role: filters.role === 'supply' ? '' : 'supply' })}
              />
              <ChipBtn
                value={t('filterPanel.roleBoth')}
                active={filters.role === 'both'}
                onClick={() => setFilters({ ...filters, role: filters.role === 'both' ? '' : 'both' })}
              />
            </div>
          </div>

          {/* Corretor */}
          {brokers.length > 0 ? (
            <div>
              <SectionHeader icon={<IdCardIcon width={14} height={14} />} title={t('filterPanel.broker')} />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                {brokers.map((b) => (
                  <ChipBtn
                    key={b.id}
                    value={b.name}
                    active={filters.broker === b.id}
                    onClick={() => setFilters({ ...filters, broker: filters.broker === b.id ? '' : b.id })}
                  />
                ))}
              </div>
            </div>
          ) : null}

          <IncomeSection t={t} filters={filters} onChange={patch} />

          <AddressSection t={t} filters={filters} onChange={patch} />

          <PreferenceSection t={t} filters={filters} onChange={patch} />

          {/* Origem */}
          <div>
            <SectionHeader icon={<UploadIcon width={14} height={14} />} title={t('filterPanel.origin')} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
              <ChipBtn
                value={t('filterPanel.importedOnly')}
                active={!!filters.imported}
                onClick={() => setFilters({ ...filters, imported: !filters.imported })}
              />
            </div>
          </div>
    </FilterPanelShell>
  );
};
